import logger from "@/dev/Logger";
import { CourseGeneration, STEP_TYPE } from "@vixi/types";
import { CourseData } from "@vixi/types";

export function questionToLessonStep(
  question: CourseGeneration.BaseQuestion,
  index = 0,
): CourseData.Step | void {
  switch (question.type) {
    case "mc":
      let mcq = question as CourseGeneration.McQuestion;
      const mcqRes: CourseData.McqStep = {
        id: index,
        order: index,
        type: STEP_TYPE.MCQ,
        multipleChoice: {
          question: mcq.question,
          options: mcq.answers,
          correctAnswer: mcq.answers.findIndex((answer) => answer === mcq.correct_answer),
        } as CourseData.McQuestion,
      };
      return mcqRes;

    case "fitb":
      let fitb = question as CourseGeneration.FitbQuestion;
      let fitbAnswer = fitb.answers.findIndex((answer) => answer === fitb.correct_answers?.blank1);

      if (fitbAnswer === -1) fitbAnswer = 0;

      const fitbRes: CourseData.FitbStep = {
        id: index,
        order: index,
        type: STEP_TYPE.FITB,
        fillInTheBlank: {
          question: fitb.question,
          options: fitb.answers,
          correctAnswer: fitbAnswer,
        } as CourseData.FitbQuestion,
      };
      return fitbRes;

    case "matching":
      let matchingQuestion = question as CourseGeneration.MatchingQuestion;
      const matchingRes: CourseData.MatchingStep = {
        id: index,
        order: index,
        type: STEP_TYPE.MATCHING,
        matching: {
          question: matchingQuestion.question,
          answers: matchingQuestion.items,
        } as CourseData.MatchingQuestion,
      };
      return matchingRes;

    default:
      logger.error(`Invalid question type: ${question.type}`);
  }
}

export function createConversationToLessonSteps(): (
  conversation: CourseGeneration.Conversation,
) => CourseData.Step[] {
  let stepIndex = 0;

  return (conversation) => {
    // Filter out messages with empty learner_message at the end
    // Also remove duplicate last messages to prevent repetition
    let filteredMessages = [...conversation.messages];
    
    // Remove trailing messages with empty learner_message
    while (
      filteredMessages.length > 0 &&
      (!filteredMessages[filteredMessages.length - 1].learner_message ||
        filteredMessages[filteredMessages.length - 1].learner_message.trim() === "")
    ) {
      filteredMessages.pop();
    }
    
    // Remove duplicate messages from the end repeatedly until no duplicates remain
    // This handles cases where there are multiple duplicate messages at the end
    let hasDuplicates = true;
    while (hasDuplicates && filteredMessages.length >= 2) {
      const lastMessage = filteredMessages[filteredMessages.length - 1];
      const secondLastMessage = filteredMessages[filteredMessages.length - 2];
      
      // Check if last two messages are identical
      if (
        lastMessage.author_message === secondLastMessage.author_message &&
        lastMessage.learner_message === secondLastMessage.learner_message
      ) {
        // Remove the duplicate (keep only one)
        filteredMessages.pop();
      } else {
        hasDuplicates = false;
      }
    }

    const convStep: CourseData.ConversationStep = {
      id: stepIndex,
      order: stepIndex,
      type: STEP_TYPE.CONVERSATION,
      messages: filteredMessages.map((message, index) => {
        return {
          authorMessage: message.author_message,
          learnerMessage: message.learner_message,
          order: index,
        };
      }),
    };
    stepIndex++;

    return [
      convStep,
      ...conversation.questions
        .map((q) => questionToLessonStep(q, stepIndex++))
        .filter((q) => q !== undefined),
    ];
  };
}

export function courseGenerationToCourse(response: CourseGeneration.Course): CourseData.Course {
  const conversationToLessonSteps = createConversationToLessonSteps();

  // Split response into chapters of 5 lessons each
  const chapters: CourseData.Unit[] = [];
  for (let i = 0; i < response.length; i += 5) {
    const chapterLessons = response.slice(i, i + 5).map((generatedLesson, lessonIndex) => {
      const lesson: CourseData.Lesson = {
        id: lessonIndex,
        order: lessonIndex,
        steps: generatedLesson.conversations.flatMap(conversationToLessonSteps),
        selectedTitle: 0,
        titles: generatedLesson.titles,
      };
      return lesson;
    });

    const chapter: CourseData.Unit = {
      id: chapters.length,
      order: chapters.length,
      lessons: chapterLessons,
    };

    chapters.push(chapter);
  }

  return {
    chapters,
  };
}
